import { m } from 'framer-motion';
// @mui
import { Container, Typography } from '@mui/material';
// components
import { MotionContainer, varBounce } from '../components/animate';
// assets
import { ForbiddenIllustration } from '../assets/illustrations';
// auth
import { useAuthContext } from './useAuthContext';
import { Role, hasAnyRole } from './types/roles';

// ----------------------------------------------------------------------

/**
 * RoleBasedGuard Component
 *
 * Legacy component for role-based access control.
 * For new code, prefer using ProtectedRoute component instead.
 *
 * @deprecated Use ProtectedRoute for new implementations
 * @see ProtectedRoute for the recommended approach
 */
type RoleBasedGuardProp = {
  /** Whether to show content when access is denied */
  hasContent?: boolean;
  /** Allowed roles (accepts both Role enum and string for backward compatibility) */
  roles?: (Role | string)[];
  /** Children to render if user has required role */
  children: React.ReactNode;
};

export default function RoleBasedGuard({ hasContent, roles, children }: RoleBasedGuardProp) {
  const { user } = useAuthContext();

  const currentRole = user?.roleId || 'guest';

  // Check if user has required role
  const hasRequiredRole = typeof roles !== 'undefined'
    ? hasAnyRole(currentRole, roles as readonly Role[])
    : true;

  if (!hasRequiredRole) {
    return hasContent ? (
      <Container component={MotionContainer} sx={{ textAlign: 'center' }}>
        <m.div variants={varBounce().in}>
          <Typography variant="h3" paragraph>
            Permission Denied
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <Typography sx={{ color: 'text.secondary' }}>
            You do not have permission to access this page
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <ForbiddenIllustration sx={{ height: 260, my: { xs: 5, sm: 10 } }} />
        </m.div>
      </Container>
    ) : null;
  }

  return <> {children} </>;
}
